import React, { useContext, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Android, Apple, OpenInNew } from "@mui/icons-material";
import { AuthContext } from "@/auth/AuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import issueOccurrencesAtom, {
  buildIssues,
  matchesFilter,
  DEFAULT_WINDOW_DAYS,
  WINDOW_OPTIONS,
  type AggregatedIssue,
  type IssueApp,
  type IssueBuild,
  type IssueDateHit,
  type IssueSort,
} from "@/atoms/issues-atom";
import getBuildsAtom from "@/atoms/get-builds-atom";

const BUILD_FETCH_LIMIT = 500;
const DAY_SECONDS = 24 * 60 * 60;
// Fixed width for the version column so run links line up across all app boxes.
const VERSION_COL_WIDTH = 200;

// Strip the boilerplate "Error detected: " prefix some failure messages carry.
const cleanMessage = (s: string): string =>
  s.replace(/^\s*error detected:\s*/i, "");

const normalizePlatform = (platform?: string): string =>
  (platform ?? "").trim().toLowerCase();

const platformLabel = (platform: string): string => {
  if (platform === "ios") return "iOS";
  if (platform === "android") return "Android";
  return platform || "Unknown";
};

// Small platform glyph, mirroring the build-selector dialog's PlatformIcon.
const PlatformIcon = ({ platform }: { platform: string }) => {
  if (platform === "ios") {
    return <Apple sx={{ fontSize: 16, color: "text.secondary" }} />;
  }
  if (platform === "android") {
    return <Android sx={{ fontSize: 16, color: "text.secondary" }} />;
  }
  return null;
};

const buildLabel = (build: IssueBuild): string => {
  const version = build.appVersionName ?? "No version";
  return build.appVersionCode
    ? `${version} (${build.appVersionCode})`
    : version;
};

// Date (no year — it's clear from the timeline) + time with am/pm.
const runLabel = (hit: IssueDateHit): string =>
  DateTime.fromMillis(hit.epoch * 1000).toFormat("LLL d, h:mm a");

// Builds a `${bundle}:${platform}` -> iconUrl lookup from the org's uploads.
// Builds arrive newest-first, so the first icon seen per key is the latest.
const useIconLookup = (companyId: string): Map<string, string> => {
  const { data: builds } = useAtomValue(
    getBuildsAtom({ organizationId: companyId, limit: BUILD_FETCH_LIMIT })
  );
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const build of builds ?? []) {
      if (!build.iconUrl || !build.bundle) continue;
      const key = `${build.bundle}:${normalizePlatform(build.platform)}`;
      if (!map.has(key)) map.set(key, build.iconUrl);
    }
    return map;
  }, [builds]);
};

// A single date the issue was seen on a build — links to that run.
const DateLink = ({ hit }: { hit: IssueDateHit }) => (
  <Typography
    component={RouterLink}
    to={`/recording/${hit.suiteId}/runs/${hit.runId}`}
    target="_blank"
    rel="noopener noreferrer"
    variant="caption"
    sx={{
      color: "primary.main",
      textDecoration: "none",
      whiteSpace: "nowrap",
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {runLabel(hit)}
    {hit.count > 1 ? ` ×${hit.count}` : ""}
  </Typography>
);

// One build row: version + build number on the left, the dates it failed on.
const BuildRow = ({ build }: { build: IssueBuild }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      gap: 2,
      px: 1.5,
      py: 0.75,
      borderTop: 1,
      borderColor: "divider",
    }}
  >
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{ width: VERSION_COL_WIDTH, flexShrink: 0 }}
      noWrap
      title={buildLabel(build)}
    >
      {buildLabel(build)}
    </Typography>
    <Box
      sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5 }}
    >
      {build.dates.map((hit) => (
        <DateLink key={`${hit.suiteId}:${hit.runId}`} hit={hit} />
      ))}
    </Box>
  </Box>
);

// One app box: icon, platform, name, bundle — then its builds.
const AppBox = ({ app, iconUrl }: { app: IssueApp; iconUrl?: string }) => (
  <Box
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1.5,
      overflow: "hidden",
      mt: 1.5,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        bgcolor: "grey.50",
      }}
    >
      <Avatar
        src={iconUrl}
        variant="rounded"
        sx={{ width: 28, height: 28, bgcolor: "grey.200" }}
      >
        {(app.appName ?? app.bundle ?? "?").charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <PlatformIcon platform={app.platform} />
          <Typography variant="body2" fontWeight={600} noWrap>
            {app.appName ?? app.bundle ?? "Unknown app"}
          </Typography>
        </Box>
        {app.bundle && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {app.bundle}
          </Typography>
        )}
      </Box>
    </Box>
    {app.builds.map((build) => (
      <BuildRow
        key={`${build.appVersionName ?? ""}|${build.appVersionCode ?? ""}`}
        build={build}
      />
    ))}
  </Box>
);

const IssueCard = ({
  issue,
  iconLookup,
}: {
  issue: AggregatedIssue;
  iconLookup: Map<string, string>;
}) => {
  const reportHref =
    issue.reportSuiteId && issue.reportRunId
      ? `/recording/${issue.reportSuiteId}/runs/${issue.reportRunId}`
      : undefined;
  const spansTests = issue.distinctTests > 1 && !!issue.clusterLabel;
  const errorText = cleanMessage(issue.statusMessage);
  // Primary (larger) text is the issue description — the error message, or the
  // root-cause label for a cross-test cluster. The test name(s) drop to a
  // smaller secondary line.
  const primaryIsError = spansTests || errorText.length > 0;
  const primaryText = spansTests
    ? issue.clusterLabel
    : errorText || issue.testName;
  const secondaryText = spansTests
    ? `Test cases: ${issue.testNames.slice(0, 4).join(", ")}${
        issue.testNames.length > 4 ? ` +${issue.testNames.length - 4} more` : ""
      }`
    : errorText
    ? `Test Case: ${issue.testName}`
    : undefined;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        overflow: "hidden",
        // In the flex-column scroll container, keep each card at its natural
        // height instead of being compressed (which would clip its content).
        flexShrink: 0,
      }}
    >
      {/* Issue description (primary) + test name (secondary) */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {reportHref ? (
              <Typography
                component={RouterLink}
                to={reportHref}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  display: "block",
                  color: primaryIsError ? "error.main" : "text.primary",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {primaryText}
              </Typography>
            ) : (
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ color: primaryIsError ? "error.main" : "text.primary" }}
              >
                {primaryText}
              </Typography>
            )}
            {secondaryText && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                {secondaryText}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Chip
              color="error"
              variant="outlined"
              label={`Found ${issue.occurrenceCount}×`}
            />
            {issue.prUrl ? (
              <Button
                variant="outlined"
                size="small"
                href={issue.prUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              >
                View PR
              </Button>
            ) : (
              <Tooltip title="Configure JIRA integration to enable this button">
                <span>
                  <Button variant="outlined" size="small" disabled>
                    Create JIRA issue
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Where it was found: a box per app, then version/build with dates */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Affected test runs
        </Typography>
        {issue.apps.map((app) => (
          <AppBox
            key={app.appKey}
            app={app}
            iconUrl={iconLookup.get(app.appKey)}
          />
        ))}
      </Box>
    </Box>
  );
};

const IssuesPage = () => {
  useDocumentTitle("Issues");
  const { user } = useContext<any>(AuthContext);
  const companyId: string = user.companyId;

  const {
    data: occurrences,
    isLoading,
    isError,
  } = useAtomValue(issueOccurrencesAtom(companyId));
  const iconLookup = useIconLookup(companyId);

  // Toolbar state.
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_WINDOW_DAYS);
  const [platform, setPlatform] = useState("");
  const [bundle, setBundle] = useState("");
  const [version, setVersion] = useState("");
  const [sort, setSort] = useState<IssueSort>("mostRecent");
  const [onlyWithPr, setOnlyWithPr] = useState(true);

  // Fix-PR url per issue, derived from the FULL fetched data (window-independent),
  // so an issue keeps its PR button even when the PR-bearing run is older than the
  // selected window.
  const prByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of occurrences ?? []) {
      if (o.prUrl && !map.has(o.issueKey)) map.set(o.issueKey, o.prUrl);
    }
    return map;
  }, [occurrences]);

  // Occurrences within the selected timeline window.
  const windowOccurrences = useMemo(() => {
    if (!occurrences) return [];
    const cutoff = Date.now() / 1000 - windowDays * DAY_SECONDS;
    return occurrences.filter((o) => o.epoch >= cutoff);
  }, [occurrences, windowDays]);

  // Cascading filter options, derived from the windowed occurrences.
  const platforms = useMemo(
    () =>
      Array.from(
        new Set(windowOccurrences.map((o) => o.platform).filter(Boolean))
      ).sort(),
    [windowOccurrences]
  );
  // Clamp each selection to what's actually available (avoids stale selections
  // lingering when the window/upstream filter changes; cascades automatically).
  const platformValue = platforms.includes(platform) ? platform : "";

  const bundles = useMemo(() => {
    const seen = new Map<string, string>();
    for (const o of windowOccurrences) {
      if (platformValue && o.platform !== platformValue) continue;
      if (!o.bundle) continue;
      if (!seen.has(o.bundle)) seen.set(o.bundle, o.appName ?? o.bundle);
    }
    return Array.from(seen, ([value, name]) => ({ value, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [windowOccurrences, platformValue]);
  const bundleValue = bundles.some((b) => b.value === bundle) ? bundle : "";

  const versions = useMemo(() => {
    const set = new Set<string>();
    for (const o of windowOccurrences) {
      if (platformValue && o.platform !== platformValue) continue;
      if (bundleValue && (o.bundle ?? "") !== bundleValue) continue;
      if (o.appVersionName) set.add(o.appVersionName);
    }
    return Array.from(set).sort();
  }, [windowOccurrences, platformValue, bundleValue]);
  const versionValue = versions.includes(version) ? version : "";

  const issues = useMemo(() => {
    const built = buildIssues(
      windowOccurrences.filter((o) =>
        matchesFilter(o, {
          platform: platformValue,
          bundle: bundleValue,
          version: versionValue,
        })
      ),
      sort,
      prByKey
    );
    return onlyWithPr ? built.filter((i) => i.prUrl) : built;
  }, [
    windowOccurrences,
    platformValue,
    bundleValue,
    versionValue,
    sort,
    onlyWithPr,
    prByKey,
  ]);

  const selectSx = { minWidth: 150 };

  // Dynamic headline: count + selected window + PR-filter state.
  const windowLabel =
    WINDOW_OPTIONS.find((o) => o.days === windowDays)?.label ??
    `${windowDays} days`;
  const headline =
    isLoading || isError
      ? "Issues"
      : `${issues.length} ${
          issues.length === 1 ? "Issue" : "Issues"
        } found in the last ${windowLabel}${
          onlyWithPr ? " with PRs created" : ""
        }`;

  return (
    <div className="flex-1 flex flex-col bg-[#E6E6E6] h-full min-w-0">
      <div className="flex flex-row flex-1 h-[calc(100vh_-_60px)] min-w-0">
        <div className="flex flex-1 gap-3 w-full bg-[#D9D9D9] p-4 min-w-0">
          <div className="flex flex-1 flex-col bg-[#E8E8E8] h-full p-4 rounded-[8px] gap-4 min-w-0 overflow-hidden">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {headline}
            </Typography>

            {/* Toolbar: timeline · app filter · sort */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={windowDays}
                onChange={(_, v) => {
                  if (v != null) setWindowDays(v);
                }}
                sx={{ bgcolor: "background.paper" }}
              >
                {WINDOW_OPTIONS.map((opt) => (
                  <ToggleButton key={opt.days} value={opt.days}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <TextField
                select
                size="small"
                label="Platform"
                value={platformValue}
                onChange={(e) => {
                  setPlatform(e.target.value);
                  setBundle("");
                  setVersion("");
                }}
                sx={selectSx}
              >
                <MenuItem value="">All platforms</MenuItem>
                {platforms.map((p) => (
                  <MenuItem key={p} value={p}>
                    {platformLabel(p)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="App"
                value={bundleValue}
                onChange={(e) => {
                  setBundle(e.target.value);
                  setVersion("");
                }}
                disabled={bundles.length === 0}
                sx={selectSx}
              >
                <MenuItem value="">All apps</MenuItem>
                {bundles.map((b) => (
                  <MenuItem key={b.value} value={b.value}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Version"
                value={versionValue}
                onChange={(e) => {
                  setVersion(e.target.value);
                }}
                disabled={versions.length === 0}
                sx={selectSx}
              >
                <MenuItem value="">All versions</MenuItem>
                {versions.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={onlyWithPr}
                    onChange={(e) => {
                      setOnlyWithPr(e.target.checked);
                    }}
                  />
                }
                label="PR created"
              />

              <Box sx={{ flexGrow: 1 }} />

              <TextField
                select
                size="small"
                label="Sort by"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as IssueSort);
                }}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="mostFound">Most found</MenuItem>
                <MenuItem value="mostRecent">Most recent</MenuItem>
              </TextField>
            </Box>

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <CircularProgress />
              </div>
            ) : isError ? (
              <div className="flex flex-1 items-center justify-center">
                <Typography color="error">
                  Failed to load issues. Please try again.
                </Typography>
              </div>
            ) : issues.length === 0 ? (
              <div className="flex flex-1 items-center justify-center">
                <Typography color="text.secondary">
                  No issues found for the selected filters.
                </Typography>
              </div>
            ) : (
              <div className="overflow-y-auto flex-1 flex flex-col gap-6 pr-1">
                {issues.map((issue) => (
                  <IssueCard
                    key={issue.key}
                    issue={issue}
                    iconLookup={iconLookup}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuesPage;
